import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Chip,
  IconButton,
} from "@mui/material";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import EditIcon from "@mui/icons-material/Edit";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import DOMPurify from "dompurify";
import api from "../../api/api";
import { ENDPOINTS } from "../../api/endpoints";
import { addSpaceAfterCommas } from "../../utils/textUtils";
import { Label } from "../../utils/Label";
import ShareMenu from "../../utils/ShareMenu";
import ShareIcon from "@mui/icons-material/Share";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
const hasValue = (v) => {
  if (v === null || v === undefined) return false;

  const str = String(v).trim();

  // Reject all zero-like strings or empty strings
  if (
    str === "" ||
    /^0+$/.test(str) || // catches "0", "00", "000000"
    /^0+(\.0+)?$/.test(str) // catches "0.0", "0.00", "000.000"
  ) {
    return false;
  }

  const num = Number(v);
  // Reject if it's NaN or non-positive
  if (isNaN(num) || num <= 0) return false;

  return true;
};

const Pipe = () => (
  <span style={{ color: "#ccc", margin: "0 4px", fontSize: "0.7rem" }}>|</span>
);

export default function NoticeCard({
  notice,
  navigate,
  formatLocations,
  loadingLocations,
  isAdmin = false,
}) {

  const [isFavorite, setIsFavorite] = useState(notice?.favorite === true);
  const [anchorEl, setAnchorEl] = useState(null);
const [shareUrl, setShareUrl] = useState("");
const [toast, setToast] = useState({
  open: false,
  message: "",
  severity: "success",
});
  const talentId = localStorage.getItem("userId");

  if (!notice) return null;


  const toggleFavorite = async () => {
    try {
      const newState = !isFavorite;

      await api.post(ENDPOINTS.saveTalentFavoriteNotice(), {
        talentId: Number(talentId),
        noticeId: notice.noticeId,
        isFavorite: newState,
      });

      setIsFavorite(newState);
    } catch (err) {
      console.error("Favorite failed", err);
    }
  };
  const showToast = (message, severity = "success") => {
  setToast({
    open: true,
    message,
    severity,
  });
};

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString() : "N/A";

  /* ---------------- CATEGORY NORMALIZATION ---------------- */
  const normalizeCategories = (cats) => {
    if (!cats) return [];
    if (Array.isArray(cats))
      return cats.filter((c) => c && c.toLowerCase() !== "string");
    if (typeof cats === "string")
      return cats
        .split(",")
        .map((c) => c.trim())
        .filter((c) => c && c.toLowerCase() !== "string");
    return [];
  };

  const categoryList = normalizeCategories(
    notice.jobCategories || notice.category,
  );

  /* ---------------- LOCATIONS ---------------- */
  const locationsText = !loadingLocations
    ? formatLocations(notice.auditionLocations || notice.locationCodes || [])
    : "";

 const openNotice = () => {
 if (!notice.noticeId) return;

  window.open(
    `/notice/${notice.noticeId}`,
    "_blank",
    "noopener,noreferrer"
  );
};

  const formatHeight = (inches) => {
    if (!inches || inches <= 0) return null;

    const ft = Math.floor(inches / 12);
    const rem = inches % 12;

    return `${ft}′${rem}″`;
  };
const openShare = (e) => {
  setAnchorEl(e.currentTarget);
  setShareUrl(
    `${window.location.origin}/casting-call/${notice.noticeId}`
  );
};

const closeShare = () => {
  setAnchorEl(null);
};
  /* ---------------- RUSH / EXPIRED ---------------- */
  const isExpired =
    notice?.noticeEndDate && new Date(notice.noticeEndDate) < new Date();

  const isRush = notice?.rushCall === true && !isExpired;

  return (
    <Box
      border={1}
      borderRadius={2}
      mb={3}
      borderColor="#ccc"
      bgcolor="#fff"
      overflow="hidden"
      position="relative"
    >
      {/* ================= TOP HEADER ================= */}
      <Box
        display="flex"
        borderBottom="1px solid #eee"
        sx={{
          backgroundColor: "#202020",
          color: "#fff",
        }}
      >
        {(isRush || isExpired) && (
          <Box
            sx={{
              width: "32px",
              backgroundColor: isExpired ? "#ff3b3b" : "#d17c7c",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                color: "#fff",
                fontSize: "13px",
                fontWeight: 700,
                writingMode: "sideways-lr",
                letterSpacing: "1px",
              }}
            >
              {isExpired ? "EXPIRED" : "RUSH CALL"}
            </Typography>
          </Box>
        )}

        <Box
          flex={1}
          p={2}
          sx={{ marginLeft: isRush || isExpired ? "4px" : 0 }}
        >
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "26px",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={openNotice}
            >
              {notice.title}
            </Typography>

            {/* SHARE + FAVORITE */}
            <Box display="flex" justifyContent="flex-end" alignItems="center">
<>
  <IconButton
    size="medium"
    onClick={openShare}
    sx={{
      color: "#fff",
      borderRadius: 1,
      "&:hover": {
        backgroundColor: "rgba(255,255,255,0.08)",
      },
    }}
  >
    <ShareIcon fontSize="medium" />
  </IconButton>

  <ShareMenu
    anchorEl={anchorEl}
    open={Boolean(anchorEl)}
    onClose={closeShare}
 shareUrl={shareUrl}
    title={notice.title}
    pay={notice.payScale}
    location={notice.locationsText}
    deadline={notice.noticeEndDate}
    onCopySuccess={() => showToast("Link copied!", "success")}
  />
</>

{/* EDIT */}
{isAdmin && (
  <IconButton
    size="medium"
    aria-label="Edit Casting"
    onClick={() =>
      navigate(`/post-casting-call/${notice.noticeId}`, {
        state: {
          casting: notice,
          noticeId: notice.noticeId,
        },
      })
    }
    sx={{
      ml: 0.5,
      color: "#fff",
      borderRadius: 1,
      "&:hover": {
        backgroundColor: "rgba(255,255,255,0.08)",
      },
    }}
  >
    <EditIcon fontSize="medium" />
  </IconButton>
)}

{/* BOOKMARK */}
<IconButton
  size="medium"
  aria-label={isFavorite ? "Remove bookmark" : "Add bookmark"}
  onClick={toggleFavorite}
  sx={{
    ml: 0.5,
    color: isFavorite ? "#d17c7c" : "#fff",
  }}
>
  {isFavorite ? (
    <BookmarkIcon fontSize="medium" />
  ) : (
    <BookmarkBorderIcon fontSize="medium" />
  )}
</IconButton>
            </Box>
          </Grid>

          {/* Categories */}
          {/*    {categoryList.length > 0 && (
            <Typography sx={{ mt: 0.5, fontSize: "15px", color: "#ccc" }}>
              Category:
              {categoryList.join(", ")}
            </Typography>
          )} */}

          {/* Seeking Talent From */}
          {locationsText && (
            <Typography sx={{ mt: 0, fontSize: "15px" }}>
              <span style={{ color: "#ccc" }}>Seeking Talent From: </span>
              <span style={{ color: "#90b5fe" }}>{locationsText}</span>
            </Typography>
          )}

          {/* Union Status */}
          {(notice.unionStatus || notice.union) && (
            <Typography sx={{ mt: 1, fontSize: "17px" }}>
              <span style={{ color: "#ccc" }}>Union Status:</span>{" "}
              {(notice.unionStatus || notice.union)
                .split(",")
                .map((u) => u.trim())
                .join(", ")}
            </Typography>
          )}

          {/* Pay */}
          {(notice.payScale || notice.pay) && (
            <Typography sx={{ mt: 0, fontSize: "17px" }}>
              <span style={{ color: "#ccc" }}>Pay:</span>{" "}
              {notice.payScale || notice.pay}
            </Typography>
          )}

          {/* Posted + Deadline */}
          <Typography sx={{ mt: 0, fontSize: "17px" }}>
            <span style={{ color: "#ccc" }}>Posted:</span>{" "}
            <span style={{ color: "#90b5fe" }}>
              {notice.noticeStartDate
                ? new Date(notice.noticeStartDate).toLocaleDateString()
                : "N/A"}
            </span>{" "}
            {(() => {
  const createdDate = notice.noticeStartDate
    ? notice.noticeStartDate.split("T")[0]
    : null;

  const updatedDate = notice.noticeUpdatedDate
    ? notice.noticeUpdatedDate.split("T")[0]
    : null;

  const showUpdated =
    createdDate &&
    updatedDate &&
    updatedDate !== "N/A" &&
    updatedDate > createdDate;

  return showUpdated ? (
    <>
      <span style={{ color: "#ccc" }}>Updated:</span>{" "}
      <span style={{ color: "#90b5fe" }}>
        {new Date(notice.noticeUpdatedDate).toLocaleDateString()}
      </span>
    </>
  ) : null;
})()}
            <span style={{ color: "#ccc", paddingLeft: 6 }}>
              Deadline for Submissions:
            </span>{" "}
            <span style={{ color: "#d17c7c" }}>
              {notice.noticeEndDate
                ? new Date(notice.noticeEndDate).toLocaleDateString()
                : "N/A"}
            </span>
          </Typography>
        </Box>
      </Box>

      {/* ================= ROLES ================= */}
      <Box>
        {(notice.roles || []).map((role, index) => {
          const isRoleAlert = role?.roleAlert === 1;
          const isLast = index === notice.roles.length - 1;
          return (
            <Box
              key={role.roleId}
              display="flex"
              sx={{
                backgroundColor: "#fff",
                borderBottom: isLast ? "none" : "1px solid #eee",
              }}
            >
              {/* LEFT STRIP */}
              <Box
                sx={{
                  backgroundColor: isRoleAlert ? "#15d398" : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  visibility: isRoleAlert ? "visible" : "hidden",
                }}
              >
                {isRoleAlert && (
                  <Typography
                    sx={{
                      color: "#fff",
                      fontSize: "11px",
                      fontWeight: 700,
                      writingMode: "sideways-lr",
                      letterSpacing: "1px",
                      p: 1,
                    }}
                  >
                    ROLE ALERT
                  </Typography>
                )}
              </Box>

              {/* RIGHT CONTENT */}
              <Box flex={1} display="flex" flexDirection="column">
                {/* TOP ROW */}
                <Box
                  display="flex"
                  alignItems="center"
                  flexWrap="wrap"
                  px={1}
                  py={0.5}
                  fontSize="1.1rem"
                >
                  <Typography
                    sx={{
                      fontSize: "1.1rem",
                      fontWeight: 500,
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                      color: "#90b5fe",
                    }}
                    onClick={openNotice}
                  >
                    {role.rolename}
                  </Typography>

                  {role.sex && (
                    <>
                      <Pipe />
                      <span style={{ fontWeight: 500 }}>
                        {addSpaceAfterCommas(role.sex)}
                      </span>
                    </>
                  )}

                  {role.ethnicity && (
                    <>
                      <Pipe />
                      <span style={{ fontWeight: 500 }}>
                        {addSpaceAfterCommas(role.ethnicity)}
                      </span>
                    </>
                  )}

                  {(hasValue(role.ageStart) || hasValue(role.ageEnd)) && (
                    <>
                      <Pipe />
                      <Label>Age:</Label>
                      <span style={{ fontWeight: 500 }}>
                        {role.ageStart}-{role.ageEnd}
                      </span>
                    </>
                  )}

                  {role.roleType && (
                    <>
                      <Pipe />
                      <Label>Role Type: </Label>
                      <span style={{ fontWeight: 500 }}>{role.roleType}</span>
                    </>
                  )}

                  {role.roleUnion && (
                    <>
                      <Pipe />
                      <span style={{ fontWeight: 500 }}>{role.roleUnion}</span>
                    </>
                  )}

                  {role.eyeColor && (
                    <>
                      <Pipe />
                      <Label>Eye Color:</Label>
                      <span style={{ fontWeight: 500 }}>
                        {addSpaceAfterCommas(role.eyeColor)}
                      </span>
                    </>
                  )}

                  {role.hairColor && (
                    <>
                      <Pipe />
                      <Label>Hair Color: </Label>
                      <span style={{ fontWeight: 500 }}>
                        {addSpaceAfterCommas(role.hairColor)}
                      </span>
                    </>
                  )}

                  {role.isSubmitted === 1 && (
                    <Chip
                      label="Applied"
                      color="success"
                      size="small"
                      sx={{ fontWeight: 600, ml: "5px" }}
                    />
                  )}
                </Box>

                {/* DETAILS */}
                {role.roleDetails && (
                  <Typography
                    component="div"
                    sx={{
                      fontSize: "16px",
                      color: "#000",
                      px: 1,
                      pb: 1,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(role.roleDetails),
                    }}
                  />
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
      <Snackbar
  open={toast.open}
  autoHideDuration={2000}
  onClose={() =>
    setToast((prev) => ({
      ...prev,
      open: false,
    }))
  }
  anchorOrigin={{
    vertical: "bottom",
    horizontal: "center",
  }}
>
  <Alert
    severity={toast.severity}
    variant="filled"
    onClose={() =>
      setToast((prev) => ({
        ...prev,
        open: false,
      }))
    }
  >
    {toast.message}
  </Alert>
</Snackbar>
    </Box>
  );
}
